import { useEffect, useRef, useState } from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import {
  Box,
  Button,
  Card,
  Icon,
  Input,
  ListRow,
  PancakeStack,
  Separator,
  Sheet,
  Text,
  VisuallyHidden,
} from '@betterment/samba-react';
import { CardLoader, SambaAutoLink } from '@betterment/shared-components';

import {
  DirectTradingAccountFinancialSecuritySearchDocument,
  DirectTradingAccountFinancialSecuritySearchQuery,
} from './graphql/directTradingAccountFinancialSecuritySearch.generated';

import { NarrowTypename } from '../../../graphql/utils/narrow-typename';
import { urlBuilder } from '../../../Shared/urlBuilder';

type FinancialSecuritiesSearchType = NarrowTypename<
  DirectTradingAccountFinancialSecuritySearchQuery['account'],
  'DirectTradingAccount'
>['financialSecuritiesSearch'];

type SearchSheetResultsRowProps = {
  accountId: string;
  searchResult: FinancialSecuritiesSearchType[number];
};

const SearchSheetResultsRow: React.FC<SearchSheetResultsRowProps> = ({ accountId, searchResult }) => {
  return (
    <>
      <SambaAutoLink
        to={urlBuilder.appAccountSecurity.absolute({
          accountId,
          securityId: searchResult.id,
        })}
        variant="ghostSecondary"
        css={{
          px: 'space.0',
          py: 'space.0',
          width: '100%',
        }}
      >
        <ListRow
          css={{
            width: '100%',
            transition: 'background-color 0.2s ease-in-out',
            backgroundColor: 'background.transparent',
            '&:hover': {
              cursor: 'pointer',
            },
          }}
        >
          <ListRow.LeadingContent>
            <Text variant="text100">{searchResult.name}</Text>
            <Text variant="text50">{searchResult.symbol}</Text>
          </ListRow.LeadingContent>
          <ListRow.TrailingAsset>
            <Icon name="CaretRight" size="small" />
          </ListRow.TrailingAsset>
        </ListRow>
      </SambaAutoLink>
      <Separator />
    </>
  );
};

type SearchSheetResultsRowsProps = {
  accountId: string;
  financialSecuritiesSearch: FinancialSecuritiesSearchType;
};

const SearchSheetResultsRows: React.FC<SearchSheetResultsRowsProps> = ({ accountId, financialSecuritiesSearch }) => {
  return (
    <>
      {financialSecuritiesSearch.map(searchResult => (
        <SearchSheetResultsRow key={searchResult.id} accountId={accountId} searchResult={searchResult} />
      ))}
    </>
  );
};

type SearchSheetResultsContainerProps = {
  accountId: string;
  searchTerm: string;
  minimumSearchTermLength: number;
};

export const SearchSheetResultsContainer: React.FC<SearchSheetResultsContainerProps> = ({
  accountId,
  searchTerm,
  minimumSearchTermLength,
}) => {
  const [shouldShowLoading, setShouldShowLoading] = useState(false);

  const { data, loading } = useGraphQLQuery(DirectTradingAccountFinancialSecuritySearchDocument, {
    variables: { accountId, searchTerm },
    throwOnError: true,
    skip: searchTerm.length < minimumSearchTermLength,
  });

  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => {
        setShouldShowLoading(true);
      }, 1000);

      return () => clearTimeout(timer);
    }

    setShouldShowLoading(false);
    return undefined;
  }, [loading]);

  if (loading && !shouldShowLoading) return null;
  if (loading && shouldShowLoading) return <CardLoader />;

  if (!data) return null;

  const { account } = data;
  const { financialSecuritiesSearch } = account;

  return (
    <Card css={{ boxShadow: 'none', width: '100%' }}>
      <Card.Content>
        {financialSecuritiesSearch.length > 0 ? (
          <SearchSheetResultsRows accountId={accountId} financialSecuritiesSearch={financialSecuritiesSearch} />
        ) : (
          <Text as="div">No results matching your search.</Text>
        )}
      </Card.Content>
    </Card>
  );
};

type SearchSheetProps = {
  accountId: string;
};

export const SearchSheet: React.FC<SearchSheetProps> = ({ accountId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTerm(userSearchTerm);
    }, 200);

    return () => clearTimeout(handler);
  }, [userSearchTerm]);

  const handleClear = () => {
    setUserSearchTerm('');

    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const minimumSearchTermLength = 1;

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        role="button"
        spacing="compact"
        variant="solidPrimary"
        css={{ width: '100%', gap: 'space.100' }}
      >
        <Button.Icon name="Search" />
        Find security
      </Button>
      <Sheet open={isOpen} onOpenChange={setIsOpen} overlay="default">
        <Sheet.Content size="large" css={{ overflow: 'scroll' }}>
          <PancakeStack>
            <PancakeStack.Header css={{ px: 'space.400', pt: 'space.400' }}>
              <Sheet.Title>Search</Sheet.Title>
              <VisuallyHidden>
                <Sheet.Description>Find Stocks and ETFs.</Sheet.Description>
              </VisuallyHidden>
            </PancakeStack.Header>
            <PancakeStack.Main
              css={{
                pt: 'space.300',
                px: 'space.400',
              }}
            >
              <Box
                css={{
                  py: 'space.150',
                  pb: 'space.400',
                  gap: 'space.300',
                }}
              >
                <Box css={{ position: 'relative' }}>
                  <Input
                    name="search"
                    role="searchbox"
                    ref={searchInputRef}
                    appearance="default"
                    value={userSearchTerm}
                    onChange={e => setUserSearchTerm(e.target.value)}
                    placeholder="Search stocks and ETFs"
                    leadingIcon="Search"
                  />

                  {userSearchTerm.length > 0 && (
                    <Box css={{ position: 'absolute', top: '25%', right: 'space.200' }}>
                      <Button variant="textPrimary" onClick={handleClear}>
                        Clear
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
              {searchTerm.length >= minimumSearchTermLength && (
                <SearchSheetResultsContainer
                  accountId={accountId}
                  searchTerm={searchTerm}
                  minimumSearchTermLength={minimumSearchTermLength}
                />
              )}
            </PancakeStack.Main>
            <Separator />
            <PancakeStack.Footer
              css={{
                padding: 'space.300',
              }}
            >
              <Sheet.Close asChild={true}>
                <Button variant="solidSecondary">Close</Button>
              </Sheet.Close>
            </PancakeStack.Footer>
          </PancakeStack>
        </Sheet.Content>
      </Sheet>
    </>
  );
};
